import * as types from '../types';
import Song from '../../models/Song';

// Initial state
// -------------
const state = {
    song:                 Song.getFromSession(),
    songLoaded:           '',
    selectedPartIndex:    0,
    selectedMeasureIndex: -1,
    editionMode:          false,
    isSongModified:       false,
};

// Getters
// -------
const getters = {
    song:                 state => state.song,
    songLoaded:           state => state.songLoaded,
    selectedPartIndex:    state => state.selectedPartIndex,
    selectedMeasureIndex: state => state.selectedMeasureIndex,
    editionMode:          state => state.editionMode,
    selectedPart:         state => state.song.parts[state.selectedPartIndex],
    isSongModified:       state => state.isSongModified,
    selectedMeasure:      state =>
    {
        const isDefined = (
            state.song.parts[state.selectedPartIndex] !== undefined &&
            state.song.parts[state.selectedPartIndex].measures[state.selectedMeasureIndex] !== undefined);

        return (isDefined)
            ? state.song.parts[state.selectedPartIndex].measures[state.selectedMeasureIndex]
            : null;
    },
};

// Mutations
// ---------
const mutations = {
    /**
     * Toggle edition mode
     *
     * @author Fabien Bellanger
     * @param {Object}    state
     */
    [types.M_SONG_TOGGLE_EDITION_MODE](state)
    {
        state.editionMode = !state.editionMode;
    },
    /**
     * Modification de l'édition mode
     *
     * @author Fabien Bellanger
     * @param {Object}    state
     * @param {Boolean}   editionMode
     */
    [types.M_SONG_SET_EDITION_MODE](state, editionMode)
    {
        state.editionMode = editionMode;
    },
    /**
     * Mise à jour d'une mesure
     *
     * @author Fabien Bellanger
     * @param {Object}  state
     * @param {Object}  payload
     */
    [types.M_SONG_EDIT_MEASURE](state, payload)
    {
        const partIndex    = (typeof payload.partIndex === 'number') ? parseInt(payload.partIndex, 10) : 0;
        const measureIndex = (typeof payload.measureIndex === 'number') ? parseInt(payload.measureIndex, 10) : -1;
        const measure      = (typeof payload.measure === 'object') ? payload.measure : null;

        state.song.editMeasure(partIndex, measureIndex, measure, state.selectedPartIndex);

        state.isSongModified = (JSON.stringify(state.song) !== state.songLoaded);
    },
    /**
     * Ajout d'une partie
     *
     * @author Fabien Bellanger
     * @param {Object}  state
     */
    [types.M_SONG_ADD_PART](state, index)
    {
        state.selectedPartIndex = state.song.addPart(index);

        state.isSongModified = (JSON.stringify(state.song) !== state.songLoaded);
    },
    /**
     * Suppression d'une partie
     *
     * @author Fabien Bellanger
     * @param {Object}  state
     */
    [types.M_SONG_DELETE_PART](state, index)
    {
        state.song.deletePart(index);

        state.isSongModified = (JSON.stringify(state.song) !== state.songLoaded);
    },
    /**
     * Modification d'une chanson
     *
     * @author Fabien Bellanger
     * @param {Object}  state
     * @param {Object}  payload
     */
    [types.M_SONG_UPDATE](state, payload)
    {
        if (payload.property === 'capo')
        {
            state.song.capo = (payload.value >= 0 && payload.value <= 21) ? +payload.value : 0;
        }
        else if (payload.property === 'tempo')
        {
            state.song.tempo = (payload.value >= 0 && payload.value <= 320) ? +payload.value : 0;
        }
        else if (payload.property === 'displayMeasureNumber')
        {
            state.song.displayMeasureNumber = !!payload.value;
        }
        else
        {
            state.song[payload.property] = payload.value;
        }

        state.isSongModified = (JSON.stringify(state.song) !== state.songLoaded);
    },
    /**
     * Modification du nom de la partie
     *
     * @author Fabien Bellanger
     * @param {Object}  state
     * @param {Object}  payload
     */
    [types.M_SONG_SAVE_PART_NAME](state, payload)
    {
        state.song.updatePartName(payload.name, payload.index);

        state.isSongModified = (JSON.stringify(state.song) !== state.songLoaded);
    },
    /**
     * Suppression d'une mesure
     *
     * @author Fabien Bellanger
     * @param {Object}  state
     * @param {Object}  payload
     */
    [types.M_SONG_DELETE_MEASURE](state, payload)
    {
        state.song.deleteMeasure(payload.partIndex, payload.measureIndex);

        state.isSongModified = (JSON.stringify(state.song) !== state.songLoaded);
    },
    /**
     * Chargement d'une chanson
     *
     * @author Fabien Bellanger
     * @param {Object}  state
     * @param {Object}  payload
     */
    [types.M_SONG_LOAD](state, payload)
    {
        state.song = payload;
        state.songLoaded = JSON.stringify(payload);
    },
    /**
     * Modification du status modifié
     *
     * @author Fabien Bellanger
     * @param {Object}   state
     * @param {Boolean}  status
     */
    [types.M_SONG_TOGGLE_IS_MODIFIED](state, status)
    {
        state.isSongModified = !!status;
    },
};

// Actions
// -------
const actions = {
    /**
     * Toogle edition mode
     *
     * @author Fabien Bellanger
     * @param {Object}   store
     */
    [types.A_SONG_TOGGLE_EDITION_MODE](store)
    {
        store.commit(types.M_SONG_TOGGLE_EDITION_MODE);
    },
    /**
     * Edition de  l'edition mode
     *
     * @author Fabien Bellanger
     * @param {Object}   store
     * @param {Boolean}  editionMode
     */
    [types.A_SONG_SET_EDITION_MODE](store, editionMode)
    {
        store.commit(types.M_SONG_SET_EDITION_MODE, editionMode);
    },
    /**
     * Ajout d'une mesure
     *
     * @author Fabien Bellanger
     * @param {Object}  store
     * @param {Object}  payload
     */
    [types.A_SONG_EDIT_MEASURE](store, payload)
    {
        store.commit(types.M_SONG_EDIT_MEASURE, payload);
        
        // Enregistrement en session
        // -------------------------
        Song.saveInSession(store.state.song);
    },
    /**
     * Ajout d'une partie
     *
     * @author Fabien Bellanger
     * @param {Object}  store
     */
    [types.A_SONG_ADD_PART](store, index)
    {
        store.commit(types.M_SONG_ADD_PART, index);
        
        // Enregistrement en session
        // -------------------------
        Song.saveInSession(store.state.song);
    },
    /**
     * Suppression d'une partie
     *
     * @author Fabien Bellanger
     * @param {Object}  store
     */
    [types.A_SONG_DELETE_PART](store, index)
    {
        store.commit(types.M_SONG_DELETE_PART, index);
        
        // Enregistrement en session
        // -------------------------
        Song.saveInSession(store.state.song);
    },
    /**
     * Modification d'une chanson
     *
     * @author Fabien Bellanger
     * @param {Object}  store
     * @param {Object}  payload
     */
    [types.A_SONG_UPDATE](store, payload)
    {
        store.commit(types.M_SONG_UPDATE, payload);
        
        // Enregistrement en session
        // -------------------------
        Song.saveInSession(store.state.song);
    },
    /**
     * Modification du nom de la partie sélectionnée
     *
     * @author Fabien Bellanger
     * @param {Object}  store
     * @param {Object}  payload
     */
    [types.A_SONG_SAVE_PART_NAME](store, payload)
    {
        store.commit(types.M_SONG_SAVE_PART_NAME, payload);
        
        // Enregistrement en session
        // -------------------------
        Song.saveInSession(store.state.song);
    },
    /**
     * Suppression d'une mesure
     *
     * @author Fabien Bellanger
     * @param {Object}  store
     * @param {Object}  payload
     */
    [types.A_SONG_DELETE_MEASURE](store, payload)
    {
        store.commit(types.M_SONG_DELETE_MEASURE, payload);
        
        // Enregistrement en session
        // -------------------------
        Song.saveInSession(store.state.song);
    },
    /**
     * Chargement d'une chanson
     *
     * @author Fabien Bellanger
     * @param {Object}  store
     * @param {Object}  payload
     */
    [types.A_SONG_LOAD](store, payload)
    {
        store.commit(types.M_SONG_LOAD, payload);
        
        // Enregistrement en session
        // -------------------------
        Song.saveInSession(store.state.song);
    },
    /**
     * Modification du status modifié
     *
     * @author Fabien Bellanger
     * @param {Object}   store
     * @param {Boolean}  status
     */
    [types.A_SONG_TOGGLE_IS_MODIFIED](store, status)
    {
        store.commit(types.M_SONG_TOGGLE_IS_MODIFIED, status);
    },
};

export default {
    state,
    getters,
    mutations,
    actions,
};
